import { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { newToast } from 'store/slices/uiSlice';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'router';
import { VerifyAccessV2 as VerifyAccessPage } from '@karehero/llama/pwa/pages/VerifyAccessV2/VerifyAccessV2';
import {
  useRegisterMemberByDomainMutation,
  useRegisterMemberByInviteMagicLinkMutation,
} from 'store/api/iam';
import {
  getTargetOrgId,
  getToken,
  getOrganisationCreationCode,
  setEmail,
  setToken,
  setOrganisationCreationCode,
  setPartnerProduct,
} from 'store/slices/registrationSlice';
import { useGetVerificationConfigurationForOrganizationQuery } from 'store/api/organization';
import { useValidateOrganisationCreationCodeMutation } from 'store/api/partnerProductCodes';
import { clearAuth } from 'store/slices/iamSlice';

export const VerifyAccess = () => {
  const organizationID = useSelector(getTargetOrgId);
  const token = useSelector(getToken);
  const stashedPartnerCode = useSelector(getOrganisationCreationCode);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [registerMemberByDomain] = useRegisterMemberByDomainMutation();
  const [registerMemberByInviteMagicLink] =
    useRegisterMemberByInviteMagicLinkMutation();
  const [validateOCC, { isLoading: isValidatingCode }] =
    useValidateOrganisationCreationCodeMutation();

  const [inviteKeyType, setInviteKeyType] = useState<string>('Payroll ID');
  const [partnerCodeError, setPartnerCodeError] = useState<string | null>(null);

  const verificationResult =
    useGetVerificationConfigurationForOrganizationQuery(organizationID, {
      skip: !organizationID,
    });

  useEffect(() => {
    if (verificationResult.error) {
      console.error(
        '[VerifyAccess] Get verification configuration error',
        verificationResult.error,
      );
    } else if (verificationResult.data) {
      setInviteKeyType(verificationResult.data?.inviteKeyName || 'Payroll ID');
    }
  }, [verificationResult]);

  useMemo(async () => {
    if (stashedPartnerCode) return;
    if (token) {
      const res = await registerMemberByInviteMagicLink({
        token,
      });

      if ('error' in res) {
        console.error('[VerifyAccess] Register by invite error', res.error);
        dispatch(setToken(null));
        return false;
      }

      if ('data' in res) {
        console.log('[VerifyAccess] Register by invite result', res.data);
        dispatch(setToken(null));
        navigate(ROUTES.VERIFY_SUCCESS);
        return true;
      }
    }
  }, [token, navigate, registerMemberByInviteMagicLink]);

  useEffect(() => {
    if (!stashedPartnerCode) return;
    (async () => {
      try {
        const result = await validateOCC({ code: stashedPartnerCode }).unwrap();
        dispatch(
          setPartnerProduct({
            id: result.partnerProduct.id,
            name: result.partnerProduct.name,
            requiresPartnerCode: result.partnerProduct.requiresPartnerCode,
            requiresLicenceCode: result.partnerProduct.requiresLicenceCode,
          }),
        );
        navigate(ROUTES.PARTNER_REGISTER);
      } catch {
        dispatch(setOrganisationCreationCode(null));
        dispatch(
          newToast({
            title: 'Error',
            description:
              'The partner code in your link is invalid or has already been used. Please check with your partner.',
            variant: 'error',
          }),
        );
      }
    })();
  }, [stashedPartnerCode, validateOCC, navigate, dispatch]);

  const onSubmit = useCallback(
    async (email: string) => {
      if (!email) return;

      dispatch(setEmail(email));
      const res = await registerMemberByDomain({
        email: email,
      });
      if ('error' in res) {
        console.error('[VerifyAccess] Register by domain error', res.error);

        dispatch(
          newToast({
            title: 'Error',
            description:
              "We don't recognise that work email. Please check and try again.",
            variant: 'error',
          }),
        );

        return false;
      }

      if ('data' in res) {
        console.log('[VerifyAccess] Register by domain result', res.data);
        dispatch(
          newToast({
            title: 'Success',
            description: 'Email verification code requested, check your inbox!',
            variant: 'success',
          }),
        );

        navigate(ROUTES.VERIFY_ENTER_CONFIRMATION);
        return true;
      }

      dispatch(
        newToast({
          title: 'Error',
          description:
            'We received an unexpected response when requesting a verification code, please try again',
          variant: 'error',
        }),
      );
    },
    [navigate, dispatch, registerMemberByDomain],
  );

  const onUseInviteKey = useCallback(() => {
    navigate(ROUTES.VERIFY_ENTER_CONFIRMATION);
  }, [navigate]);

  const onPartnerCode = useCallback(
    async (code: string): Promise<boolean> => {
      setPartnerCodeError(null);
      try {
        const result = await validateOCC({ code }).unwrap();
        dispatch(setOrganisationCreationCode(code));
        dispatch(
          setPartnerProduct({
            id: result.partnerProduct.id,
            name: result.partnerProduct.name,
            requiresPartnerCode: result.partnerProduct.requiresPartnerCode,
            requiresLicenceCode: result.partnerProduct.requiresLicenceCode,
          }),
        );
        navigate(ROUTES.PARTNER_REGISTER);
        return true;
      } catch (err: any) {
        const isServerError =
          typeof err?.status === 'number' && err.status >= 500;

        const message = isServerError
          ? 'Something went wrong. Please try again later.'
          : err?.data?.message || 'Invalid code. Please check and try again.';
        setPartnerCodeError(message);
        return false;
      }
    },
    [validateOCC, navigate, dispatch],
  );

  const onRefreshAuth = useCallback(() => {
    dispatch(clearAuth());
    navigate(ROUTES.HOME);
  }, [navigate, dispatch]);

  return (
    <VerifyAccessPage
      onRefreshAuth={onRefreshAuth}
      onSubmit={onSubmit}
      onUseInviteKey={onUseInviteKey}
      onPartnerCode={onPartnerCode}
      inviteKeyName={inviteKeyType}
      partnerCodeError={partnerCodeError}
      isValidatingCode={isValidatingCode}
    />
  );
};

VerifyAccess.displayName = 'VerifyAccess';
export default VerifyAccess;
